<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow" :modal-loading="loading"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" modal-class="mobile-full-screen">

      <div class="row mb-4">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Files') }}</h5>
            </div>
            <div class="card-body">
              <table class="info-table table table-sm files-table mt-0 mb-0">
                <tr>
                  <th class="border-top-0">{{ $t('File Name') }}</th>
                  <th class="border-top-0">{{ $t('Comment') }}</th>
                </tr>
                <tr v-for="(fileModel, idx) in fileModels" :key="`preview-modal-file-${idx}`">
                  <td>
                    <input-widget :model="fileModel" attribute="name" :label="false"/>
                  </td>
                  <td>
                    <input-widget :model="fileModel" attribute="comment" :label="false"/>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-2" v-if="isDocusignEnabled">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h5 class="d-inline-block mb-0">
                {{ $t('Recipients') }}
              </h5>
              <b-button class="float-right p-0" variant="link"
                        @click="onAddNewRecipientClick(true)">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Add New Recipient') }}
              </b-button>
            </div>
            <div class="card-body pb-0">
              <div class="row">
                <div class="col-md-12">

                  <div class="row">
                    <div class="col-md-2 font-weight-bold">{{ $t('Type') }}</div>
                    <div class="col-md-3 font-weight-bold">{{ $t('Filter') }}</div>
                    <div class="col-md-2 font-weight-bold">{{ $t('Email') }}</div>
                    <div class="col-md-2 font-weight-bold">{{ $t('Name') }}</div>
                    <div class="col-md-2 font-weight-bold">{{ $t('Routing Order') }}</div>
                    <div class="col-md-1 font-weight-bold">{{ $t('Actions') }}</div>
                    <div class="col-md-12">
                      <hr class="mt-2 mb-2">
                    </div>
                  </div>

                  <div v-for="(recipient, idx) in model.recipients" :key="`preview-modal-recipient-${recipient.uuid}`" class="row">
                    <div class="col-md-2">
                      <input-widget :model="recipient" attribute="type" value-field="value"
                                    :label="false" :options="{placeholder: $t('Recipient Type')}"
                                    :disabled="recipient.disabled"
                                    @select2Select="onTypeSelect(recipient)"
                                    @select2Unselect="onTypeUnselect(recipient)"
                                    display-field="${item.key}" type="select2" :items="recipientTypes"/>
                    </div>
                    <div class="col-md-3">
                      <customer-select-input-widget attribute="customer_uuid" :model="recipient" :label="false"
                                                    v-if="recipient.type === 'customer'"
                                                    @select="onCustomerSelect($event, recipient)"
                                                    @unselect="onCustomerUnselect(recipient)"
                                                    :disabled="recipient.disabled"/>
                      <user-select-input-widget attribute="user_uuid" :model="recipient" :label="false"
                                                @select="onUserSelect($event, recipient)"
                                                @unselect="onUserUnselect(recipient)"
                                                v-else-if="recipient.type === 'user'" :disabled="recipient.disabled"/>
                      <input-widget v-else type="text" :model="recipient" :label="false" disabled attribute="unknown"/>
                    </div>
                    <div class="col-md-2">
                      <input-widget :model="recipient" attribute="email" :label="false" :placeholder="true"
                                    :disabled="recipient.disabled || ['customer', 'user'].includes(recipient.type)"/>
                    </div>
                    <div class="col-md-2">
                      <input-widget :model="recipient" attribute="display_name" :label="false" :placeholder="true"
                                    :disabled="recipient.disabled || ['customer', 'user'].includes(recipient.type)"/>
                    </div>
                    <div class="col-md-2">
                      <input-widget :model="recipient" attribute="routing_order" :label="false" type="number"
                                    :placeholder="true"/>
                    </div>
                    <div class="col-md-1">
                      <b-button class="text-danger text-white" variant="danger" @click="onRecipientRemoveClick(idx)"
                                :disabled="recipient.disabled">
                        <font-awesome-icon class="text-white" icon="times" style="font-size: .9em"></font-awesome-icon>
                      </b-button>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import ContractSend from "@/modules/contracts/ContractSend.model";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import ContractFileModel from "@/modules/contracts/ContractFile.model";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget.vue";
  import UserSelectInputWidget from "@/core/widgets/UserSelectInputWidget.vue";
  import RecipientModel from "@/modules/contracts/Recipient.model";
  import CustomerService from "@/modules/customer/CustomerService";
  import {uuid} from 'vue-uuid';

  const {mapState, mapActions} = createNamespacedHelpers('contracts');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ContractFilesPreviewModal",
    components: {
      UserSelectInputWidget,
      CustomerSelectInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        model: new ContractSend(),
        showConfirmation: false,
        loading: false,
        fileModels: [],
        isHiddenFromSubmit: false,
        recipientTypes: [
          {key: this.$i18n.t('Customer'), value: "customer"},
          {key: this.$i18n.t('User'), value: "user"},
          {key: this.$i18n.t('Free'), value: "free"},
        ]
      }
    },
    computed: {
      ...mapState({
        modal: state => state.contractFilesPreviewModal,
        customerUuid: state => state.contractFilesPreviewModal.customerUuid,
        files: state => state.contractFilesPreviewModal.files,
      }),
      computedTitle() {
        return this.$i18n.t('Preview Files')
      },
      ...globalMapState([
        'isDocusignEnabled',
        'currentBranch',
      ]),
    },
    watch: {
      async files() {
        if (this.files && this.files.length > 0) {
          for (let fileObj of this.files) {
            this.fileModels.push(new ContractFileModel({
              name: fileObj.file.name.split('.')[0],
              fileObj: fileObj
            }))
          }
        }
      },
    },
    methods: {
      ...mapActions(['hideContractFilesPreviewModal']),
      async onShow() {
        this.showConfirmation = true
        this.isHiddenFromSubmit = false

        this.loading = true
        let resp = await CustomerService.getCustomer(this.customerUuid)
        if (resp.success) {
          this.model.recipients.push(new RecipientModel({
            uuid: uuid.v4(),
            customer_uuid: resp.body.uuid,
            customer: resp.body,
            type: 'customer',
            disabled: true,
            email: resp.body.email,
            display_name: resp.body.display_name,
          }))
        }

        if (this.currentBranch.contract_default_recipients && this.currentBranch.contract_default_recipients.length > 0) {
          this.currentBranch.contract_default_recipients.forEach(r => {
            this.model.recipients.push(new RecipientModel({
              uuid: uuid.v4(),
              type: 'free',
              email: r.email,
              display_name: r.display_name,
              routing_order: r.routing_order
            }))
          })
        }

        this.loading = false
      },
      onHidden() {
        this.fileModels = []
        this.hideContractFilesPreviewModal();
        if (!this.isHiddenFromSubmit) {
          this.$emit('clearData')
        }
        this.model = new ContractSend()
      },
      async onSubmit() {
        this.showConfirmation = false

        let files = []

        this.fileModels.forEach(fm => files.push({
          comment: fm.comment,
          file: new File([fm.fileObj.file], `${fm.name}.${fm.fileObj.file.name.split('.')[1]}`, {
            type: fm.fileObj.file.type,
            lastModified: fm.fileObj.file.lastModified
          })
        }))

        let recipients = this.model.recipients.map(r => {
          return {
            customer_uuid: r.customer_uuid,
            user_uuid: r.user_uuid,
            display_name: r.display_name,
            email: r.email,
            routing_order: r.routing_order,
          }
        })

        const emailMap = {}

        for (const { email } of recipients) {
          emailMap[email] = (emailMap[email] || 0) + 1
        }
        const duplicateEmails = Object.keys(emailMap).filter(
          email => emailMap[email] > 1
        )

        if (duplicateEmails.length > 0) {
          this.$errorDialog(this.$i18n.t('You cannot submit contract with duplicate email recipients. Please, remove duplicates and try again. Duplicate email(s): {emails}', {emails: duplicateEmails.join(', ')}))
          return
        }

        this.isHiddenFromSubmit = true
        this.$emit('onModalSubmit', {files, recipients})
        this.hideContractFilesPreviewModal()
      },
      onAddNewRecipientClick() {
        this.model.recipients.push(new RecipientModel({
          uuid: uuid.v4(),
        }))
      },
      onRecipientRemoveClick(idx) {
        this.model.recipients = this.model.recipients.filter((_, i) => i !== idx)
      },
      onCustomerSelect(customer, recipient) {
        recipient.email = customer.email
        recipient.display_name = customer.display_name
      },
      onCustomerUnselect(recipient) {
        recipient.email = null
        recipient.display_name = null
        recipient.customer_uuid = null
      },
      onUserSelect(user, recipient) {
        recipient.email = user.email
        recipient.display_name = user.display_name
      },
      onUserUnselect(recipient) {
        recipient.email = null
        recipient.display_name = null
        recipient.user_uuid = null
      },
      onTypeSelect(recipient) {
        recipient.display_name = null
        recipient.user_uuid = null
        recipient.email = null
        recipient.customer_uuid = null
        recipient.customer = null
        recipient.user = null
      },
      onTypeUnselect(recipient) {
        recipient.display_name = null
        recipient.user_uuid = null
        recipient.email = null
        recipient.customer_uuid = null
        recipient.customer = null
        recipient.user = null
      }
    }
  }
</script>

<style lang="scss" scoped>
  .files-table {
    /deep/ .form-group {
      margin-bottom: 0;
    }
  }
</style>
